import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ProfileCompletionModal from '../components/auth/ProfileCompletionModal';
import ForcePasswordChangeModal from '../components/auth/ForcePasswordChangeModal';
import SessionExpiryBanner from '../components/common/SessionExpiryBanner';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  // Force password change (when admin reset password)
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorPending, setTwoFactorPending] = useState(false);

  // Listen for the 423 signal from the global axios interceptor.
  useEffect(() => {
    const handler = () => setForcePasswordChange(true);
    window.addEventListener('marq:force-password-change', handler);
    return () => window.removeEventListener('marq:force-password-change', handler);
  }, []);

  // Listen for the 401 "session expired" signal from the global interceptor.
  // The interceptor has already cleared localStorage; we flip React state,
  // show one toast, and route the user back to the entry point so they can
  // sign in again instead of sitting in a zombie "logged in but broken" UI.
  useEffect(() => {
    const handler = () => {
      // Skip if we were already signed out (e.g. user just logged out manually).
      if (!localStorage.getItem('token') && !user && !token) return;
      setToken(null);
      setUser(null);
      setShowProfileCompletion(false);
      setRequires2FA(false);
      setTwoFactorPending(false);
      setForcePasswordChange(false);
      delete axios.defaults.headers.common['Authorization'];
      toast.error('Your session has expired. Please sign in again.', {
        id: 'session-expired',  // dedupe across re-renders
        duration: 5000,
      });
      // Don't hard-redirect — the route guards (AuthenticatedRoute / AdminRoute)
      // automatically render their built-in "Sign in to continue" card when
      // `user` becomes null. This keeps the Toaster mounted so the toast is
      // actually visible to the user.
    };
    window.addEventListener('marq:session-expired', handler);
    return () => window.removeEventListener('marq:session-expired', handler);
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
      setAuthLoading(false);
    }
  }, [token]);

  // Mirror the caller's role + id into localStorage so surfaces that can't
  // reach into AuthContext (namely the top-level ErrorBoundary, which is a
  // class component and lives *above* the provider in the tree, or renders
  // when the provider itself has crashed) can still read the current role
  // synchronously. Purely a read cache — never used as an auth source.
  useEffect(() => {
    try {
      if (user?.role) {
        localStorage.setItem('user_role', user.role);
      } else {
        localStorage.removeItem('user_role');
      }
      if (user?.id) {
        localStorage.setItem('user_id', user.id);
      } else {
        localStorage.removeItem('user_id');
      }
    } catch (_e) {
      // localStorage disabled (private mode, etc.) — silently skip.
    }
  }, [user]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      
      // Backend-driven forced password change (admin reset)
      if (response.data?.password_change_required) {
        setForcePasswordChange(true);
      }
      
      // Check if profile needs completion
      if (response.data && !response.data.profile_completed) {
        setShowProfileCompletion(true);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Only logout on 401 (unauthorized/token expired), not on other errors like network issues or server errors
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
      setAuthLoading(false);
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email,
        password,
        name
      });
      const { access_token, user: userData } = response.data;
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Check if profile needs completion
      if (userData && !userData.profile_completed) {
        setShowProfileCompletion(true);
      }
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  // New login function for INDoS-based login
  const loginWithIndos = async (indosNumber, password, rememberMe = false) => {
    try {
      // Get screen info for device fingerprinting
      const screenInfo = `${window.screen.width}x${window.screen.height}`;
      
      const response = await axios.post(`${API}/auth/new-login`, {
        indos_number: indosNumber,
        password: password,
        remember_me: rememberMe,
        screen_info: screenInfo
      });
      const { access_token, user: userData, session_id, is_new_device, device_name, security_events, requires_2fa, two_factor_pending } = response.data;
      
      // Store token and session ID
      setToken(access_token);
      localStorage.setItem('token', access_token);
      localStorage.setItem('session_id', session_id);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Show security notifications
      if (security_events && security_events.length > 0) {
        security_events.forEach(event => {
          if (event.type === 'device_limit_exceeded') {
            // Delay notification slightly so it appears after login
            setTimeout(() => {
              console.info(`Security: Device limit reached. ${event.logged_out_device} was logged out.`);
            }, 1000);
          }
        });
      }
      
      // Check for 2FA requirement
      if (requires_2fa || two_factor_pending) {
        setRequires2FA(true);
        setTwoFactorPending(true);
        setUser(userData);
        return { success: true, requires2FA: true, user: userData };
      }
      
      setUser(userData);
      
      // Backend-driven forced password change (admin reset)
      if (userData?.password_change_required) {
        setForcePasswordChange(true);
      }
      
      // Check if profile needs completion
      if (userData && !userData.profile_completed) {
        setShowProfileCompletion(true);
      }
      
      return { success: true, requires2FA: false, is_new_device, device_name };
    } catch (error) {
      const errorData = error.response?.data?.detail;
      
      // Check if it's a session limit error (403 with session_limit_exceeded)
      if (error.response?.status === 403 && errorData?.session_limit_exceeded) {
        return { 
          success: false, 
          error: errorData.message || 'You are already logged in on another device.',
          sessionLimitExceeded: true,
          activeSessions: errorData.active_sessions,
          maxSessions: errorData.max_sessions
        };
      }
      
      // Check if it's a maintenance mode error (503)
      if (error.response?.status === 503 && errorData?.maintenance_mode) {
        return {
          success: false,
          error: errorData.message || 'Site is under maintenance.',
          maintenanceMode: true
        };
      }
      
      // Generic error handling
      return { 
        success: false, 
        error: typeof errorData === 'string' ? errorData : (errorData?.message || 'Login failed') 
      };
    }
  };

  // Complete 2FA verification
  const complete2FAVerification = (userData = null) => {
    setRequires2FA(false);
    setTwoFactorPending(false);
    // If user data is provided, set it directly instead of fetching
    if (userData) {
      setUser(userData);
    } else {
      // Fetch user data to properly authenticate after 2FA verification
      fetchUser();
    }
  };

  // Cancel 2FA (logout)
  const cancel2FA = () => {
    setRequires2FA(false);
    setTwoFactorPending(false);
    logout();
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setShowProfileCompletion(false);
    setRequires2FA(false);
    setTwoFactorPending(false);
    setForcePasswordChange(false);
    localStorage.removeItem('token');
    localStorage.removeItem('session_id');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_id');
    delete axios.defaults.headers.common['Authorization'];
  };

  const isAuthenticated = () => {
    // Not authenticated if 2FA is pending
    if (twoFactorPending) {
      return false;
    }
    return !!token && !!user;
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const handleProfileComplete = (updatedUser) => {
    setUser(updatedUser);
    setShowProfileCompletion(false);
    // Refresh the page to update all components
    window.location.reload();
  };

  const value = {
    user,
    token,
    loading,
    authLoading,
    register,
    loginWithIndos,
    logout,
    isAuthenticated,
    isAdmin,
    setUser,
    setToken,
    // Re-hydrate user from /auth/me — used by post-payment flows so plan
    // details in the header/profile update without a hard page reload.
    refreshUser: fetchUser,
    // 2FA
    requires2FA,
    twoFactorPending,
    complete2FAVerification,
    cancel2FA
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionExpiryBanner />
      {showProfileCompletion && (
        <ProfileCompletionModal
          open={showProfileCompletion}
          user={user}
          onComplete={handleProfileComplete}
        />
      )}
      {forcePasswordChange && (
        <ForcePasswordChangeModal
          open={forcePasswordChange}
          currentEmail={user?.email}
        />
      )}
    </AuthContext.Provider>
  );
};